import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner, Nav } from 'react-bootstrap';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import ProfileDetails from '../components/profile/ProfileDetails';
import AccountSettings from '../components/profile/AccountSettings';
import SecuritySettings from '../components/profile/SecuritySettings';
import ChangePasswordModal from '../components/modals/ProfileModals/ChangePasswordModal';
import ChangeEmailModal from '../components/modals/ProfileModals/ChangeEmailModal';
import PaymentModal from '../components/modals/ProfileModals/PaymentModal';
import SubscriptionModal from '../components/modals/ProfileModals/SubscriptionModal';
import TermsModal from '../components/modals/ProfileModals/TermsModal';
import BillingHistoryModal from '../components/modals/ProfileModals/BillingHistoryModal';
import { fetchAccountDetails, updateUserProfile, checkPaymentStatus, uploadProfilePicture } from '../services/profile.service';
import { useAuth } from '../contexts/AuthContext';
import ProfileImage from '../components/profile/ProfileImage';
import { invalidateProfileImageCache } from '../components/profile/ProfileImage';
import defaultProfileImage from '../assets/images/default.png';
import PaymentStatusChecker from '../services/PaymentStatusChecker';
import PendingPaymentIndicator from '../components/PendingPaymentIndicator';

import '../styles/pages/profile.css';

const Profile = () => {
  // State variables
  const [sidebarToggled, setSidebarToggled] = useState(false);
  const [activeSection, setActiveSection] = useState('profile');
  const [editMode, setEditMode] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [accountDetails, setAccountDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', variant: 'success' });
  const [imageUploading, setImageUploading] = useState(false);

  const fileInputRef = useRef(null);

  // Modal states
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [changeEmailModalOpen, setChangeEmailModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [billingHistoryModalOpen, setBillingHistoryModalOpen] = useState(false);

  const [pendingPayments, setPendingPayments] = useState([]);
  const [paymentCheckers, setPaymentCheckers] = useState([]);

  const isLoadingRef = useRef(false);

  // Get auth context
  const authContext = useAuth();
  const { updateUser } = useAuth();
  const { token: authToken } = authContext || {};

  // Get token from session storage or auth context
  const getToken = useCallback(() => {
    // First try auth context token
    if (authToken) {
      return authToken;
    }

    // Fallback to session storage token
    const sessionToken = sessionStorage.getItem('userToken');
    return sessionToken;
  }, [authToken]);

  // Load user details
  useEffect(() => {

    const token = getToken();
    if (!token) {
      setError('Authentication token not found. Please log in again.');
      setLoading(false);
      return;
    }

    // Check for various conditions that require data refresh
    const urlParams = new URLSearchParams(window.location.search);
    const hasPaymentParams = urlParams.get('reference') || urlParams.get('trxref');
    const paymentSuccess = sessionStorage.getItem('paymentSuccess') === 'true';
    const forceRefresh = sessionStorage.getItem('forceDataRefresh') === 'true';

    // Determine if we need to force refresh
    const shouldForceRefresh = hasPaymentParams || paymentSuccess || forceRefresh;

    // Clean up flags and URL params
    if (hasPaymentParams) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    if (paymentSuccess) {
      showAlert('Payment successful! Your subscription has been updated.', 'success');
      sessionStorage.removeItem('paymentSuccess');
    }

    if (forceRefresh) {
      sessionStorage.removeItem('forceDataRefresh');
    }

    // Clear cache if forcing refresh
    if (shouldForceRefresh) {
      sessionStorage.removeItem('accountDetails');
      sessionStorage.removeItem('userDetails');
    }

    // Load user data
    loadUserData(token, shouldForceRefresh);
  }, [authToken, getToken]);

  useEffect(() => {
    // Only invalidate cache if we have userDetails and this is after an image upload
    if (userDetails?.profileImageVersion) {
      invalidateProfileImageCache();
    }
  }, [userDetails?.profileImageVersion]);

  useEffect(() => {
    const token = getToken();
    if (!token) return;

    // Check for pending payments and restore polling
    if (PaymentStatusChecker.hasPendingPayments()) {
      const checkers = PaymentStatusChecker.restoreFromStorage(token, {
        onSuccess: (data) => {
          console.log('Crypto payment confirmed during polling restoration:', data);
          showAlert('Cryptocurrency payment confirmed! Your subscription has been updated.', 'success');

          // Refresh user data
          loadUserData(token, true);

          // Remove from pending payments
          setPendingPayments(prev => prev.filter(p => p.paymentId !== data.paymentId));
        },

        onExpired: (paymentId) => {
          console.log('Crypto payment expired during polling restoration');
          showAlert('A pending cryptocurrency payment has expired.', 'warning');
          setPendingPayments(prev => prev.filter(p => p.paymentId !== paymentId));
        },

        onError: (error, paymentId) => {
          console.error('Crypto payment error during polling restoration:', error);
          setPendingPayments(prev => prev.filter(p => p.paymentId !== paymentId));
        },

        onStatusUpdate: (status, data, paymentId) => {
          setPendingPayments(prev =>
            prev.map(p =>
              p.paymentId === paymentId
                ? { ...p, status, data }
                : p
            )
          );
        }
      });

      setPaymentCheckers(checkers);

      // Set initial pending payments state
      setPendingPayments(
        checkers.map(checker => ({
          paymentId: checker.paymentId,
          status: 'pending',
          data: null
        }))
      );
    }

    // Cleanup function
    return () => {
      paymentCheckers.forEach(checker => checker.stop());
    };
  }, [getToken]);

  const loadUserData = async (token, forceRefresh = false) => {
    if (!token) {
      setError('No authentication token provided');
      setLoading(false);
      return;
    }

    // Prevent concurrent calls
    if (isLoadingRef.current) {
      return;
    }

    isLoadingRef.current = true;
    setLoading(true);
    setError(null);

    try {

      // Get email from session storage
      let email = null;
      try {
        const storedUserDetails = sessionStorage.getItem('userDetails');
        if (storedUserDetails) {
          const parsedDetails = JSON.parse(storedUserDetails);
          email = parsedDetails.Email;
        }
      } catch (e) {
        console.error('Error parsing user details from session storage:', e);
      }

      if (!email) {
        // Check localStorage for saved email (from Paystack redirect)
        email = localStorage.getItem('userEmail');
        if (email) {
        } else {
          console.warn('No email found in session storage or localStorage, will use server default');
          email = 'default@example.com';
        }
      }

      // Fetch account details with retry logic
      let retryCount = 0;
      const maxRetries = 3;
      let data = null;

      while (retryCount < maxRetries && !data) {
        try {
          data = await fetchAccountDetails(token, email);
          break; // Success, exit retry loop
        } catch (error) {
          retryCount++;
          console.error(`Profile - API call attempt ${retryCount} failed:`, error);

          if (retryCount >= maxRetries) {
            throw error; // Re-throw after max retries
          }

          // Wait before retrying (exponential backoff)
          const waitTime = Math.pow(2, retryCount) * 1000; // 2s, 4s, 8s
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
      }

      if (!data) {
        throw new Error('Failed to fetch account details after multiple attempts');
      }

      // Update state with fetched data
      setUserDetails(data.user);
      setAccountDetails(data);

      // Update session storage with fresh data
      sessionStorage.setItem('userDetails', JSON.stringify(data.user));
      sessionStorage.setItem('accountDetails', JSON.stringify(data));

      // Dispatch event to notify other components
      document.dispatchEvent(new CustomEvent('user-subscription-updated'));

    } catch (error) {
      console.error('Profile - Error loading user data:', error);

      // More specific error messages
      let errorMessage = 'Error loading profile data';
      if (error.message.includes('timeout')) {
        errorMessage = 'Request timed out. Please check your connection and try again.';
      } else if (error.message.includes('Network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error. Please try again in a moment.';
      } else {
        errorMessage = `Error loading profile: ${error.message}`;
      }

      setError(errorMessage);
      showAlert(errorMessage, 'danger');
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  // Show alert helper
  const showAlert = (message, variant = 'success') => {
    setAlert({ show: true, message, variant });

    // Auto dismiss after 5 seconds
    setTimeout(() => {
      setAlert(prev => ({ ...prev, show: false }));
    }, 5000);
  };

  const handleProfileImageClick = (e) => {
    // Stop event propagation to prevent any parent handlers from interfering
    e.stopPropagation();

    // Explicitly check that fileInputRef exists and has current property
    if (fileInputRef && fileInputRef.current) {
      // Use direct DOM method to ensure browser compatibility
      fileInputRef.current.click();
    } else {
      console.error('File input reference is not available');
    }
  };

  const handleProfileImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validImageTypes.includes(file.type)) {
      showAlert('Please select a valid image file (JPEG, PNG, GIF, WEBP)', 'danger');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      showAlert('Image size must be less than 5MB', 'danger');
      return;
    }

    setImageUploading(true);

    try {
      const token = getToken();
      if (!token) {
        showAlert('Authentication token not found. Please log in again.', 'danger');
        setImageUploading(false);
        return;
      }

      const apiBaseUrl = 'https://phenixloc-api.fnvt.net';

      // Step 1: TRY to DELETE the existing profile image (but don't fail if there isn't one)
      try {
        const deleteResponse = await fetch(`${apiBaseUrl}/api/user/profile-image`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });


      } catch (deleteError) {
        // Log the error but continue with upload
      }

      // Step 2: Upload the new profile image
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${apiBaseUrl}/api/user/upload-profile-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();

      if (response.ok && result.message) {
        const imageVersion = Date.now();

        const updatedUserDetails = {
          ...userDetails,
          profileImageVersion: imageVersion
        };

        // Update state first
        setUserDetails(updatedUserDetails);
        sessionStorage.setItem('userDetails', JSON.stringify(updatedUserDetails));
        updateUser({ profileImageVersion: imageVersion });

        // IMPORTANT: Remove the immediate invalidateProfileImageCache() call
        // It will be called from the useEffect below after state updates

        showAlert('Profile picture updated successfully', 'success');
      } else {
        throw new Error(result.error || result.message || 'Failed to update profile picture');
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      showAlert(`Error updating profile picture: ${error.message}`, 'danger');
    } finally {
      setImageUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle section changes
  const handleSectionChange = (section) => {
    setActiveSection(section);
    setEditMode(false);
  };

  // Handle edit mode
  const toggleEditMode = () => {
    setEditMode(!editMode);
  };

  // Handle form submission for profile update
  const handleProfileUpdate = async (formData, e) => {
    if (e && e.preventDefault) e.preventDefault();
    const token = getToken();
    if (!token) {
      showAlert('Authentication token not found. Please log in again.', 'danger');
      return;
    }

    try {
      const result = await updateUserProfile(formData, token);

      if (result.success) {
        showAlert('Profile updated successfully', 'success');

        const updatedUserDetails = {
          ...userDetails,
          ...formData
        };

        setUserDetails(updatedUserDetails);
        sessionStorage.setItem('userDetails', JSON.stringify(updatedUserDetails));
        updateUser(formData);

        const userUpdatedEvent = new CustomEvent('user-profile-updated', {
          detail: formData // Send just the updated fields
        });
        document.dispatchEvent(userUpdatedEvent);

        setEditMode(false);
      } else {
        throw new Error(result.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      showAlert(`Error updating profile: ${error.message}`, 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Handle payment process
  const handlePayNow = async () => {
    const token = getToken();
    if (!token) {
      showAlert('Authentication token not found. Please log in again.', 'danger');
      return;
    }

    try {
      // First check if terms are accepted
      const isTermsAccepted = sessionStorage.getItem('paymentTermsAccepted') === 'true';

      if (!isTermsAccepted) {
        setTermsModalOpen(true);
      } else {
        // Set selectedPlan to null when paying for current subscription
        setSelectedPlan(null);
        setPaymentModalOpen(true);
      }
    } catch (error) {
      console.error('Error initiating payment:', error);
      showAlert(`Error initiating payment: ${error.message}`, 'danger');
    }
  };

  // Handle subscription plan change
  const handleChangePlan = () => {
    setSubscriptionModalOpen(true);
  };

  const handleViewBillingHistory = () => {
    setBillingHistoryModalOpen(true);
  };

  // Handle plan selection
  const handlePlanSelection = (plan) => {
    setSelectedPlan(plan);
    setSubscriptionModalOpen(false);

    // Check if terms are accepted
    const isTermsAccepted = sessionStorage.getItem('paymentTermsAccepted') === 'true';

    if (!isTermsAccepted) {
      setTermsModalOpen(true);
    } else {
      setPaymentModalOpen(true);
    }
  };

  // Handle terms acceptance
  const handleTermsAcceptance = () => {
    sessionStorage.setItem('paymentTermsAccepted', 'true');
    setTermsAccepted(true);
    setTermsModalOpen(false);
    setPaymentModalOpen(true);
  };

  // Handle login redirect
  const handleRelogin = () => {
    sessionStorage.clear();
    window.location.href = '/login';
  };

  return (
    <div id="page-top">
      <div id="wrapper" className="d-flex">
        <Sidebar toggled={sidebarToggled} setToggled={setSidebarToggled} />

        <div id="content-wrapper" className={`d-flex flex-column ${sidebarToggled ? 'content-toggled' : ''}`}>
          <div id="content">
            <DashboardHeader toggleSidebar={() => setSidebarToggled(!sidebarToggled)} />

            <div className="container-fluid">
              {/* Alert */}
              {alert.show && (
                <Alert
                  variant={alert.variant}
                  onClose={() => setAlert(prev => ({ ...prev, show: false }))}
                  dismissible
                  className="mt-3"
                >
                  {alert.message}
                </Alert>
              )}

              {/* Pending Crypto Payments */}
              {pendingPayments.length > 0 && (
                <div className="pending-payments-section mt-3">
                  {pendingPayments.map((payment) => (
                    <PendingPaymentIndicator
                      key={payment.paymentId}
                      status={payment.status}
                      paymentId={payment.paymentId}
                      transactionHash={payment.data?.transactionHash}
                      onDismiss={() => {
                        if (payment.status === 'completed' || payment.status === 'expired') {
                          setPendingPayments(prev =>
                            prev.filter(p => p.paymentId !== payment.paymentId)
                          );
                        }
                      }}
                    />
                  ))}
                </div>
              )}

              {/* Error Display */}
              {error && (
                <Alert variant="danger" className="mt-3">
                  <Alert.Heading>Error Loading Profile</Alert.Heading>
                  <p>{error}</p>
                  {error.includes('Authentication') && (
                    <div className="mt-3">
                      <Button variant="primary" onClick={handleRelogin}>
                        Go to Login
                      </Button>
                    </div>
                  )}
                </Alert>
              )}

              {/* Loading state */}
              {loading && (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="success" />
                  <p className="mt-3">Loading profile data...</p>
                </div>
              )}

              {/* Content - only show when not loading and no error */}
              {!loading && !error && userDetails && (
                <Container fluid className="profile-container p-md-4 p-0">
                  <Row className="profile-row">
                    {/* Mobile Profile Header - Only visible on small screens */}
                    <Col xs={12} className="d-md-none mb-4">
                      <Card className="profile-card-mobile text-center">
                        <div className="profile-header-mobile pt-3 pb-2">
                          <div className="profile-image-container mx-auto">
                            <ProfileImage
                              version={userDetails?.profileImageVersion}
                              src={userDetails?.profileImage}
                              defaultSrc={defaultProfileImage}
                              className="rounded-circle"
                              onClick={handleProfileImageClick}
                              style={{ cursor: 'pointer', width: '150px', height: '150px' }}
                            />
                            <div
                              className="profile-image-overlay"
                              onClick={handleProfileImageClick}
                            >
                              <i className="fas fa-camera"></i>
                            </div>
                            <input
                              type="file"
                              id="profile-pic"
                              ref={fileInputRef}  // Add this ref
                              accept="image/*"
                              style={{ display: 'none' }}
                              onChange={handleProfileImageChange}
                            />

                            {/* Loading overlay - only show when uploading */}
                            {imageUploading && (
                              <div className="profile-image-loading">
                                <div className="spinner-border text-primary" role="status">
                                  <span className="sr-only">Loading...</span>
                                </div>
                              </div>
                            )}
                          </div>
                          <h5 className="mt-3 text-black">{userDetails?.FullName || 'Loading...'}</h5>
                          <p className="text-black-50">{userDetails?.Email || ''}</p>
                        </div>

                        {/* Mobile Navigation Tabs */}
                        <Card.Body className="p-0">
                          <Nav variant="tabs" className="profile-nav-tabs">
                            <Nav.Item>
                              <Nav.Link
                                active={activeSection === 'profile'}
                                onClick={() => handleSectionChange('profile')}
                              >
                                <i className="fas fa-user mr-1"></i>
                                <span className="d-none d-sm-inline">Profile</span>
                              </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                              <Nav.Link
                                active={activeSection === 'account'}
                                onClick={() => handleSectionChange('account')}
                              >
                                <i className="fas fa-credit-card mr-1"></i>
                                <span className="d-none d-sm-inline">Account</span>
                              </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                              <Nav.Link
                                active={activeSection === 'security'}
                                onClick={() => handleSectionChange('security')}
                              >
                                <i className="fas fa-shield-alt mr-1"></i>
                                <span className="d-none d-sm-inline">Security</span>
                              </Nav.Link>
                            </Nav.Item>
                          </Nav>
                        </Card.Body>
                      </Card>
                    </Col>

                    {/* Desktop Sidebar - Only visible on medium screens and up */}
                    <Col md={3} className="d-none d-md-block profile-sidebar-col">
                      <Card className="profile-sidebar-card sticky-top">
                        <div className="text-center pt-4 pb-3">
                          <div className="profile-image-container mx-auto">
                            <ProfileImage
                              version={userDetails?.profileImageVersion}
                              src={userDetails?.profileImage}
                              defaultSrc={defaultProfileImage}
                              className="rounded-circle"
                              alt="Profile"
                              onClick={handleProfileImageClick}
                              style={{ cursor: 'pointer', width: '150px', height: '150px' }}
                            />
                            <div
                              className="profile-image-overlay"
                              onClick={handleProfileImageClick}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 2,
                                opacity: 0.7,
                                background: 'rgba(0,0,0,0.3)',
                                borderRadius: '50%'
                              }}
                            >
                              <i className="fas fa-camera"></i>
                            </div>
                          </div>
                          <h5 className="mt-3 text-black">{userDetails?.FullName || 'Loading...'}</h5>
                          <p className="text-black-50">{userDetails?.Email || ''}</p>
                        </div>
                        <Nav className="flex-column profile-side-nav">
                          <Nav.Link
                            className={activeSection === 'profile' ? 'active' : ''}
                            onClick={() => handleSectionChange('profile')}
                          >
                            <i className="fas fa-user mr-2"></i> Profile Details
                          </Nav.Link>
                          <Nav.Link
                            className={activeSection === 'account' ? 'active' : ''}
                            onClick={() => handleSectionChange('account')}
                          >
                            <i className="fas fa-credit-card mr-2"></i> Account Settings
                          </Nav.Link>
                          <Nav.Link
                            className={activeSection === 'security' ? 'active' : ''}
                            onClick={() => handleSectionChange('security')}
                          >
                            <i className="fas fa-shield-alt mr-2"></i> Security Settings
                          </Nav.Link>
                        </Nav>
                      </Card>
                    </Col>

                    {/* Main Content Area */}
                    <Col xs={12} md={7} className="profile-content-col">
                      <Card className="profile-content-card">
                        <Card.Header className="d-flex justify-content-between align-items-center bg-white border-bottom-0">
                          <h4 className="mb-0">
                            {activeSection === 'profile' && 'Profile Details'}
                            {activeSection === 'account' && 'Account Settings'}
                            {activeSection === 'security' && 'Security Settings'}
                          </h4>
                          {activeSection === 'profile' && (
                            <Button
                              variant="success"
                              size="sm"
                              onClick={toggleEditMode}
                              className="btn-rounded"
                            >
                              {editMode ? (
                                <><i className="fas fa-eye mr-1"></i> View</>
                              ) : (
                                <><i className="fas fa-pencil-alt mr-1"></i> Edit</>
                              )}
                            </Button>
                          )}
                        </Card.Header>
                        <Card.Body>
                          {/* Profile Content Section */}
                          {activeSection === 'profile' && (
                            <ProfileDetails
                              userDetails={userDetails}
                              editMode={editMode}
                              onSave={handleProfileUpdate}
                              onCancel={() => setEditMode(false)}
                              loading={loading}
                            />
                          )}

                          {/* Account Content Section */}
                          {activeSection === 'account' && (
                            <AccountSettings
                              accountDetails={accountDetails}
                              onPayNow={handlePayNow}
                              onChangePlan={handleChangePlan}
                              onViewBillingHistory={handleViewBillingHistory}
                              loading={loading}
                            />
                          )}

                          {/* Security Content Section */}
                          {activeSection === 'security' && (
                            <SecuritySettings
                              onChangePassword={() => setChangePasswordModalOpen(true)}
                              onChangeEmail={() => setChangeEmailModalOpen(true)}
                              loading={loading}
                            />
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Container>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ChangePasswordModal
        show={changePasswordModalOpen}
        onHide={() => setChangePasswordModalOpen(false)}
        token={getToken()}
        showAlert={showAlert}
      />

      <ChangeEmailModal
        show={changeEmailModalOpen}
        onHide={() => setChangeEmailModalOpen(false)}
        token={getToken()}
        userEmail={userDetails?.Email}
        showAlert={showAlert}
        onSuccess={() => loadUserData(getToken())}
      />

      <PaymentModal
        show={paymentModalOpen}
        onHide={() => setPaymentModalOpen(false)}
        token={getToken()}
        selectedPlan={selectedPlan}
        userDetails={userDetails}
        accountDetails={accountDetails}
        showAlert={showAlert}
        onSuccess={() => loadUserData(getToken())}
      />

      <SubscriptionModal
        show={subscriptionModalOpen}
        onHide={() => setSubscriptionModalOpen(false)}
        token={getToken()}
        currentSubscriptionType={accountDetails?.payment?.SubscriptionType}
        onSelectPlan={handlePlanSelection}
      />

      <TermsModal
        show={termsModalOpen}
        onHide={() => setTermsModalOpen(false)}
        onAccept={handleTermsAcceptance}
      />

      <BillingHistoryModal
        show={billingHistoryModalOpen}
        onHide={() => setBillingHistoryModalOpen(false)}
        token={getToken()}
      />
    </div>
  );
};

export default Profile;